import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import moment from 'moment'

import './AlarmDropdown.scss';
import { ReactComponent as IconArrowUp } from '../assets/icons/ic-arrow-up.svg';
import { ReactComponent as IconArrowDown } from '../assets/icons/ic-arrow-down.svg';
import { ReactComponent as IconArrowUpGray } from '../assets/icons/ic-arrow-up-gray.svg';
import { ReactComponent as IconArrowDownGray } from '../assets/icons/ic-arrow-down-gray.svg';
import { ReactComponent as IconCheckWhite } from '../assets/icons/ic-simple-check-white.svg';
import { ReactComponent as IconCheckBlack } from '../assets/icons/ic-simple-check-black.svg';
import { ReactComponent as IconCheckBlue } from '../assets/icons/ic-simple-check-blue.svg';

const AlarmDropdown = ({ options, placeholder, isOneBed = false, isHalf, isPlayer, isAcknowledged, acknowledgeRemainingSeconds, getAlarmAcknowledgeStatus }) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const selectRef = useRef(null); // Select 컴포넌트 참조 추가
    // const [alarmStatus, setAlarmStatus] = useState(
    //     options.reduce((acc, alarm) => {
    //         acc[alarm.message] = true;
    //         return acc;
    //     }, {})
    // );    
    
    //Rodney - 중복 제거 삭제 (IBP1,2,3,4가 동일한 메시지로 들어올 수 있음.)
    const [alarmStatus, setAlarmStatus] = useState(
        options.reduce((acc, alarm) => {
            acc[alarm.message] = true;
            return acc;
        }, {})
    );

    const formatRemainingTime = (seconds) => {
        if (!seconds || seconds <= 0) return '00:00';
        
        const duration = moment.duration(seconds, 'seconds');
        const minutes = Math.floor(duration.asMinutes());
        const remainingSeconds = duration.seconds();
        
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };    

    const getDefaultAlarm = () => {
        // 1) High
        const dangerAlarms = options.filter((alarm) => alarm.type === 'High');
        if (dangerAlarms.length > 0) return dangerAlarms[0];

        // 2) Medium
        const mediumAlarms = options.filter((alarm) => alarm.type === 'Medium');
        if (mediumAlarms.length > 0) return mediumAlarms[0];

        // 3) Low
        const lowAlarms = options.filter((alarm) => alarm.type === 'Low');
        if (lowAlarms.length > 0) return lowAlarms[0];

        // 4) Normal
        const normalAlarms = options.filter((alarm) => alarm.type === 'Normal');
        if (normalAlarms.length > 0) return normalAlarms[0];

        return options.length > 0 ? options[0] : null;
    };

    const [selectedAlarm, setSelectedAlarm] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const intervalRef = useRef(null);    
    const prevOptionsRef = useRef(options);    
    const internalOptions = useMemo(() => {
        if (JSON.stringify(prevOptionsRef.current) !== JSON.stringify(options)) {
            prevOptionsRef.current = options;
            return options;
        }
        return prevOptionsRef.current;
    }, [options]);
    const isSingleOption = internalOptions.length === 1;

    // 알람 3초마다 순회
    useEffect(() => {
        if (isOneBed && internalOptions.length > 1) {
            // 기존 interval이 있으면 먼저 정리
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            
            intervalRef.current = setInterval(() => {
                setSelectedAlarm((prev) => {
                    // 최신 internalOptions를 사용하기 위해 함수형 업데이트 사용
                    const currentOptions = internalOptions;
                    const currentIndex = currentOptions.findIndex((o) => o.id === prev?.id);
                    const nextIndex = (currentIndex + 1) % currentOptions.length;
                    return currentOptions[nextIndex];
                });
            }, 3000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isOneBed, internalOptions]);

    useEffect(() => {
        const defaultAlarm = getDefaultAlarm();
        setSelectedAlarm(defaultAlarm);
    }, [internalOptions]);

    const value = internalOptions.find((o) => o.id === selectedAlarm?.id) || null;

    // 컴포넌트의 스타일에 display 적용
    const containerStyle = {
        display: isVisible ? 'block' : 'none',
    };
    
    const DropdownIndicator = (props) => {
        const Icon =
            menuIsOpen
                ? selectedAlarm && selectedAlarm.type !== 'High'
                    ? IconArrowUpGray
                    : IconArrowUp
                : selectedAlarm && selectedAlarm.type !== 'High'
                    ? IconArrowDownGray
                    : IconArrowDown;

        return (
            <components.DropdownIndicator {...props}>
                <Icon />
            </components.DropdownIndicator>
        );
    };

    const SingleValue = (props) => {
        const flattenMessage = (message) => {
            if (Array.isArray(message)) {
                return message
                    .map((item) => flattenMessage(item))
                    .join(', ');
            } else if (typeof message === 'object') {
                return Object.entries(message)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(', ');
            } else {
                return message;
            }
        };
    
        const formattedMessage = flattenMessage(props.data.message);
    
        const { options } = props.selectProps;
        const totalAlarms = options.length;
        const currentIndex = options.findIndex(
            (alarm) => alarm.id === props.data.id
        ) + 1;
    
        return (
            <components.SingleValue {...props} className="flex justify-between w-full">
                <div className="flex items-center">
                    {
                        getAlarmAcknowledgeStatus && getAlarmAcknowledgeStatus(props.data.id, props.data.eventPriority) &&
                        (props.data.type === 'High' ? 
                        <IconCheckWhite /> : <IconCheckBlack />)
                    }
                    <span
                        style={{
                            display: 'inline-block',
                            maxWidth: isPlayer ? '400px' : `${isOneBed ? '650px' : '400px'}`,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {formattedMessage}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {
                        isAcknowledged &&
                        <div className="flex items-center px-[6px] gap-[2px] bg-gradient-to-b from-[#323232] to-[#1D1D1D]" style={{ minWidth: 66, maxWidth: 66, minHeight: isHalf ? 18 : 20, maxHeight: isHalf ? 18 : 20, border: '1px solid black', borderRadius: 4,  }}>
                            <IconCheckBlue />
                            <div className="text-white" style={{ fontSize: 12, fontWeight: 500 }}>{formatRemainingTime(acknowledgeRemainingSeconds)}</div>
                        </div>
                    }
                    {!isSingleOption && <div className="ml-" style={{ minWidth: 30 }}>({currentIndex}/{totalAlarms})</div>}
                </div>
            </components.SingleValue>
        );
    };
    // 옵션이 하나일 때 DropdownIndicator 제거
    const componentsProp = {
        DropdownIndicator: isSingleOption ? null : DropdownIndicator,
        IndicatorSeparator: () => null,
        SingleValue,
    };

    const customStyles = {
        container: (provided) => ({
            ...provided,
            flexGrow: 1,
            maxHeight: isHalf ? '20px' : '',
        }),
        control: (provided) => {
            // High / (Medium|Low) / Normal 으로 구분
            const isHigh = selectedAlarm?.type === 'High';
            const isMediumOrLow = selectedAlarm?.type === 'Medium' || selectedAlarm?.type === 'Low';
            const isNormal = selectedAlarm?.type === 'Normal';
            let background = '#FFFFFF';
            let color = 'black';
            if (isHigh) {
                background = '#FF002E';
                color = 'white';
            } else if (isMediumOrLow) {
                background = 'linear-gradient(180deg, #FFF500 0%, #999300 100%)';
                color = 'black';
            } else if (isNormal) {
                background = '#FFFFFF'; 
                color = 'black';
            }
            return {
                ...provided,
                boxShadow: 'none',
                cursor: isSingleOption ? 'default' : 'pointer',
                minHeight: 'none',
                height: isHalf ? '20px' : '32px',
                lineHeight: isHalf ? '15px' : '',
                borderRadius: '0px',
                border: 'none',
                background,
                color,
                zIndex: 999,
            };
        },
        valueContainer: (provided) => ({
            ...provided,
            height: isHalf ? '20px' : '32px',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: isHalf ? '20px' : '32px',
        }),
        singleValue: (provided) => {
            const isHigh = selectedAlarm?.type === 'High';
            const isMediumOrLow = selectedAlarm?.type === 'Medium' || selectedAlarm?.type === 'Low';
            const isNormal = selectedAlarm?.type === 'Normal';
            let textColor = 'black';
            if (isHigh) {
                textColor = 'white';
            } else if (isMediumOrLow) {
                textColor = 'black';
            } else if (isNormal) {
                textColor = 'black';
            }
            return {
                ...provided,
                color: textColor,
                whiteSpace: 'nowrap',
                padding: '3px 1px 2px 8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            };
        },
        option: (provided, state) => {
            const { type } = state.data; // state.data.type 확인
            let textColor = '#FFFFFF'; // 0인 경우 흰색
            if (type === 'High') {
                textColor = '#FF002E';
            } else if (type === 'Medium' || type === 'Low') {
                textColor = '#FFF500';
            } else if (type === 'Normal') {
                textColor = '#FFFFFF';
            }
            return {
                ...provided,
                color: textColor,
                display: 'flex',
                height: '32px',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderRadius: 4,
                backgroundColor: '#1F1F1F',
                '&:active': {
                    backgroundColor: state.isSelected ? '#1F1F1F' : '#333333',
                },
            };
        },
        menu: (provided, state) => {
            let adjustedStyle = {
                ...provided,
                className: 'customMenu',
                backgroundColor: '#1F1F1F',
                border: '1px solid #5E5E5E',
                borderRadius: '5px',
                maxHeight: '345px',
                overflowY: 'hidden',
                width: '516px',
                cursor: 'pointer',
                zIndex: 9999,
            };

            // 메뉴가 화면 오른쪽 또는 아래쪽으로 잘리는지 확인하고 위치 조정
            if (selectRef.current) {
                const selectRect = selectRef.current.getBoundingClientRect();
                const menuWidth = 516;
                const menuHeight = Math.min(options.length * 32 + 16 + 16, 345) // 내부 상하 패딩 16, 외부 상하 마진 16
                
                // 가장 가까운 스크롤 가능한 부모 또는 팝업 컨테이너 찾기
                const findScrollContainer = (element) => {
                    if (!element || element === document.body) return null;
                    
                    const style = window.getComputedStyle(element);
                    const overflow = style.overflow + style.overflowX + style.overflowY;
                    
                    // 스크롤 가능하거나 팝업/모달로 보이는 요소 확인
                    if (
                        style.position === 'fixed'
                    ) {
                        return element;
                    }
                    
                    return findScrollContainer(element.parentElement);
                };
                
                const scrollContainer = findScrollContainer(selectRef.current.parentElement);
                
                let rightBoundary = window.innerWidth;
                let bottomBoundary = window.innerHeight;
                
                // 스크롤 컨테이너가 있으면 그 컨테이너의 경계 사용
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    rightBoundary = containerRect.right;
                    bottomBoundary = containerRect.bottom;
                }
                
                let transformX = 0;
                let transformY = 0;
                
                // 메뉴가 오른쪽 경계를 벗어나는 경우
                if (selectRect.left + menuWidth > rightBoundary) {
                    transformX = -(selectRect.left + menuWidth - rightBoundary + 8); // 8px 여유 공간
                }
                
                // 메뉴가 아래쪽 경계를 벗어나는 경우 위쪽으로 표시
                if (selectRect.bottom + menuHeight > bottomBoundary) {
                    // 드롭다운을 select 위쪽에 표시
                    transformY = -(selectRect.height + menuHeight);
                }
                
                // transform 적용
                if (transformX !== 0 || transformY !== 0) {
                    adjustedStyle.transform = `translate(${transformX}px, ${transformY}px)`;
                }
            }

            return adjustedStyle;
        },
        menuList: (provided) => ({
            ...provided,
            maxHeight: '345px',
            overflowY: 'auto',
            padding: '8px',
            zIndex: 9999,
            '::-webkit-scrollbar': {
                width: '4px',
            },
            '::-webkit-scrollbar-thumb': {
                backgroundColor: '#5E5E5E',
                borderRadius: '10px',
            },
            '::-webkit-scrollbar-track': {
                backgroundColor: '#1F1F1F',
            },
        }),
    };

    const handleIconClick = (alarmMessage, e) => {
        e.stopPropagation(); // 드롭다운이 닫히지 않도록 막음
        setAlarmStatus((prev) => ({
            ...prev,
            [alarmMessage]: !prev[alarmMessage],
        }));
    };

    const formatOptionLabel = (data) => {
        const flattenMessage = (message) => {
            if (Array.isArray(message)) {
                return message
                    .map((item) => flattenMessage(item)) // 배열의 각 요소를 재귀적으로 처리
                    .join(', ');
            } else if (typeof message === 'object') {
                return Object.entries(message)
                    .map(([key, value]) => `${key}: ${value}`) // 객체를 "key: value" 형태로 변환
                    .join(', ');
            } else {
                return message; // 문자열 또는 기타 타입 그대로 반환
            }
        };

        const formattedMessage = flattenMessage(data.message);
        return (
            <div
                onClick={(e) => handleIconClick(data.message, e)}
                className="flex justify-between items-center w-full"
            >
                <div className="flex items-center">
                    {
                        getAlarmAcknowledgeStatus && getAlarmAcknowledgeStatus(data.id, data.eventPriority) ?
                        <IconCheckBlue className="mr-[16px]"/> : 
                        <span className="w-[4px] h-[4px] bg-[#9F9F9F] block rounded-full mr-[16px]"></span>
                    }
                    <span style={{ fontSize: 18 }}>{formattedMessage}</span>
                </div>
                <span className="text-[#CECECE]" style={{ fontSize: 16 }}>{ moment(data.startTime).format('HH:mm') }</span>
            </div>
        );
    };

    const menuIsOpenProp = isSingleOption ? false : menuIsOpen;

    return (
        <div ref={selectRef} style={containerStyle}>
            <Select
                //options={uniqueOptions}
                options={options}
                value={value}
                placeholder={placeholder}
                styles={customStyles}
                components={componentsProp}
                formatOptionLabel={formatOptionLabel}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                isSearchable={false}
                onChange={(selected) => setSelectedAlarm(selected)}
                maxMenuHeight={345}
                className="alarm-dropdown-container font-normal"
                menuIsOpen={menuIsOpenProp}
                openMenuOnClick={!isSingleOption}
                openMenuOnFocus={!isSingleOption}
            />
        </div>
    );
};

export default AlarmDropdown;
